import React, { useEffect, useState, ChangeEvent } from 'react';

import { Table } from 'components/Table';
import {
  BenchmarkConfirmationModal,
  Button,
  Input,
  SearchInput,
} from 'components';
import { useTable } from './hooks';
import { Api } from 'api';
import { showSuccess } from 'helpers/showSuccess';
import { Container, Controls, TableWrapper } from './ScheduledJobsStyles';
import { Footer } from '../ManageJobsStyles';
import { ControlsSearch } from 'modules/Benchmarks/BenchmarkList/BenchmarkListStyles';
import { DEFAULT_TABLE_SEARCH_NO_DATA_MESSAGE } from 'components/Table/contants';

type Props = {
  edit: (id: string) => void;
  onClose: () => void;
};

export const ScheduledJobs: React.FC<Props> = ({ edit, onClose }) => {
  const [newName, setNewName] = useState('');
  const [cloneError, setCloneError] = useState('');
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const { mutate, isSuccess, isLoading }: any = Api.jobs.EditJob();
  const {
    mutate: deleteJob,
    isSuccess: deleteJobSuccess,
    isLoading: deleteJobLoading,
  } = Api.jobs.DeleteJob();
  const { table, refetch, isJobsLoading } = useTable({
    onClick,
    handleAction,
    isLoading,
    searchInput,
  });
  const { refetch: refetchJob, data: job }: any = Api.jobs.GetJob(selectedJob, {
    enabled: false,
  });
  const {
    mutate: createJob,
    isSuccess: createJobSuccess,
    isLoading: createJobLoading,
    error,
    reset,
  }: any = Api.jobs.CreateJob();

  useEffect(() => {
    if (job) {
      setNewName(job.data.name);
    }
  }, [job]);

  useEffect(() => {
    if (createJobSuccess) {
      reset();
      setShowCloneModal(false);
      showSuccess('Job was successfully cloned');
    }
  }, [createJobSuccess, reset]);

  useEffect(() => {
    if (isSuccess || deleteJobSuccess || createJobSuccess) {
      refetch();
    }
  }, [isSuccess, deleteJobSuccess, createJobSuccess, refetch]);

  useEffect(() => {
    if (deleteJobSuccess) {
      setShowDeleteModal(false);
      showSuccess('Job was successfully deleted.');
    }
  }, [deleteJobSuccess]);

  function onClick(action: boolean, id: any) {
    mutate({
      id,
      action: action ? 'enable' : 'disable',
    });
  }

  function handleAction(action: string, row: any) {
    const id = row.row.original.id;
    if (action === 'Delete') {
      setSelectedJob(id);
      setShowDeleteModal(true);
    } else if (action === 'Clone') {
      setSelectedJob(id);
      setNewName('');
      setShowCloneModal(true);
      setTimeout(() => {
        refetchJob();
      }, 0);
    } else if (action === 'Edit') {
      edit(id);
    }
  }

  const handleDeleteJob = () => {
    deleteJob(selectedJob.toString());
  };

  const handleCloneJob = () => {
    if (newName?.length > 0) {
      let body = {
        ...job.data,
        name: newName,
        jobTypeId: job.data.jobType.id,
        benchmarkIds: job.data.benchmarks.map((item: any) => {
          return item.id;
        }),
      };

      if (job.data.jobFrequency) {
        body.jobFrequencyId = job.data.jobFrequency.id;
      }

      createJob(body);
    } else {
      setCloneError('Name is required');
    }
  };

  useEffect(() => {
    if (newName?.length > 0) {
      setCloneError('');
    }
  }, [newName]);

  return (
    <Container>
      <Controls>
        <ControlsSearch>
          <SearchInput
            placeholder='Search'
            onInput={(value) => setSearchInput(value.toLowerCase())}
          />
        </ControlsSearch>
      </Controls>
      <TableWrapper>
        <Table
          data={table.data}
          columns={table.columns}
          pagination
          autoReset={false}
          selector='.modal.show'
          isLoading={isJobsLoading}
          noResultsMessage={
            !isLoading ? DEFAULT_TABLE_SEARCH_NO_DATA_MESSAGE : ''
          }
        />
      </TableWrapper>
      <Footer>
        <Button onClick={onClose} variant='secondary' data-testid='back-button'>
          Back to Benchmarks
        </Button>
      </Footer>
      <BenchmarkConfirmationModal
        isLoading={createJobLoading}
        size='sm'
        saveLabel='Save'
        show={showCloneModal}
        onClose={() => {
          setShowCloneModal(false);
          reset();
        }}
        onSave={handleCloneJob}
        title='Clone'
      >
        <Input
          label='Name of Job'
          value={newName}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setNewName(e.target.value);
          }}
          error={cloneError || error?.response?.data?.detail}
          errorMessage={cloneError || error?.response?.data?.detail}
        />
      </BenchmarkConfirmationModal>
      <BenchmarkConfirmationModal
        isLoading={deleteJobLoading}
        size='sm'
        saveLabel='Delete'
        show={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
        }}
        onSave={handleDeleteJob}
        title='Delete Scheduled Job'
      >
        Are you sure you'd like to delete this job?
      </BenchmarkConfirmationModal>
    </Container>
  );
};
